import React, { useEffect, useState } from "react";
import mermaid from "mermaid";
import { Box, Theme } from "@mui/material";

mermaid.initialize({
  startOnLoad: false,
  theme: "default",
  securityLevel: "loose",
  fontFamily: "inherit",
  flowchart: {
    useMaxWidth: true,
    htmlLabels: true,
    curve: "basis"
  }
});

let mermaidCounter = 0;

interface MermaidDiagramProps {
  /** The raw Mermaid diagram source. */
  chart: string;
}

const containerSx = (theme: Theme) => ({
  display: "flex",
  justifyContent: "center",
  m: "24px 0",
  p: 3,
  backgroundColor: theme.palette.mode === "dark" ? "#1a2332" : "#f8faff",
  borderRadius: "8px",
  border: `1px solid ${theme.palette.divider}`,
  overflow: "auto",
  "& svg": { maxWidth: "100%", height: "auto" }
});

/**
 * Render a Mermaid diagram to inline SVG. Supports both the synchronous
 * callback API (mermaid 9.1.x) and the Promise API (mermaid 9.4+/10).
 */
const MermaidDiagram: React.FC<MermaidDiagramProps> = ({ chart }) => {
  const [svg, setSvg] = useState<string>("");

  useEffect(() => {
    let cancelled = false;

    mermaidCounter += 1;
    const id = `mermaid-${mermaidCounter}`;

    const apply = (rendered: string) => {
      if (!cancelled && rendered) setSvg(rendered);
    };

    try {
      const result = mermaid.render(id, chart, (svgCode: string) => apply(svgCode));

      if (result && typeof result.then === "function") {
        result
          .then((out: { svg: string } | string) => apply(typeof out === "string" ? out : out.svg))
          .catch(() => undefined);
      } else if (typeof result === "string") {
        apply(result);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("Mermaid render error:", err);
    }

    return () => {
      cancelled = true;
    };
  }, [chart]);

  if (!svg) return null;

  // eslint-disable-next-line react/no-danger
  return <Box sx={containerSx} dangerouslySetInnerHTML={{ __html: svg }} />;
};

export default MermaidDiagram;
